import React from 'react';

import {useDispatch, useSelector} from 'react-redux';

import {GenericPaginatedList} from 'app/components/general/lists/GenericPaginatedList';
import {useAccountSettings} from 'app/settings/api';
import {setTeamMemberAccess} from 'app/teams/actions';
import {useRemoveTeamItem} from 'app/teams/api';
import {setTeamDetailPageFilter} from 'app/teams/slice';
import {getMembership} from 'app/teams/utils';
import {StoredUsersFetcher} from 'app/user/datafetchers';
import {gt} from 'app/utils/i18n';
import {useAsyncActions} from 'app/utils/redux';

import {MembersListOptionsBar} from './MembersListOptionsBar';
import {TeamMemberListItem} from './TeamMemberListItem';
import {TeamRolesList} from './TeamRolesList';

import type {MenuItemsType} from 'app/general/widgets/ThreeDotsDropdownMenu';
import type {TeamSerializedType, UserSerializedType} from 'sweet-types';

interface Props {
  team: TeamSerializedType;
  onFetch: () => unknown;
}

export function TeamMembersSubPage({team, onFetch}: Props) {
  const dispatch = useDispatch();
  const {data: settings} = useAccountSettings(undefined, {
    refetchOnMountOrArgChange: 900,
  });

  const actions = useAsyncActions({
    setTeamMemberAccess,
  });

  const [removeTeamitem] = useRemoveTeamItem();

  const showRoles = useSelector(({teams}) => {
    const filters = teams.filters.teamDetailPages[team.id.toString()] || {};
    return filters.showRoles ?? true;
  });

  const memberActionsGetter = (user: UserSerializedType): MenuItemsType => {
    const membership = getMembership(user, team);

    const loadingIfHasNoMembership: any =
      membership == null
        ? {
            text: gt('Loading...'),
            icon: 'fa fa-circle-notch fa-spin',
            disabled: true,
          }
        : {};
    return [
      {
        text: gt('Team Manager'),
        icon: `far fa-${membership?.access == 'manager' ? 'check-' : ''}square`,
        key: 'manager',
        disabled: membership?.access == 'manager',
        condition: () => team.can_edit,
        callback: () =>
          actions.setTeamMemberAccess(membership!, 'manager').then(onFetch),
        ...loadingIfHasNoMembership,
      },
      {
        text: gt('Team Member'),
        key: 'teammember',
        icon: `far fa-${
          membership?.access == 'teammate' ? 'check-' : ''
        }square`,
        disabled: membership?.access == 'teammate',
        condition: () => team.can_edit,
        callback: () =>
          actions.setTeamMemberAccess(membership!, 'teammate').then(onFetch),
        ...loadingIfHasNoMembership,
      },
      {
        text: gt('Read Only Member'),
        key: 'reader',
        icon: `far fa-${membership?.access == 'reader' ? 'check-' : ''}square`,
        disabled: membership?.access == 'reader',
        condition: () => team.can_edit,
        callback: () =>
          actions.setTeamMemberAccess(membership!, 'reader').then(onFetch),
        ...loadingIfHasNoMembership,
      },
      {
        text: gt('Remove from Team'),
        key: 'remove',
        icon: 'fa fa-times',
        condition: () => team.can_edit,
        callback: () =>
          removeTeamitem({teamURL: team.url, item: user}).then(onFetch),
        ...loadingIfHasNoMembership,
      },
    ];
  };

  return (
    <>
      <MembersListOptionsBar
        team={team}
        showingRoles={showRoles}
        setShowRoles={() =>
          dispatch(
            setTeamDetailPageFilter({
              teamid: team.id,
              filter: {
                showRoles: !showRoles,
              },
            })
          )
        }
      />
      {showRoles ? (
        <TeamRolesList team={team} memberActionsGetter={memberActionsGetter} />
      ) : (
        <GenericPaginatedList<
          UserSerializedType,
          Record<string, unknown>,
          {
            team: TeamSerializedType;
            canEdit: boolean;
            accountOwnerEmail?: string;
            actionsGetter: any;
          },
          Record<string, unknown>
        >
          ItemComponent={TeamMemberListItem}
          FetcherComponent={StoredUsersFetcher}
          noItemsMessage={() => gt('No members for this team')}
          paramsBuilder={() => ({
            team_id: team.id,
          })}
          customProps={() => ({
            team,
            canEdit: team ? team.can_edit : false,
            accountOwnerEmail: settings?.owner_email,
            actionsGetter: memberActionsGetter,
          })}
        />
      )}
    </>
  );
}
